import { LiveEditorStepKey } from "../Steps/LiveEditorStep.enum";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { TICKETING_SELECTION_V2_RATE_ID_PREFIX } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/Rates";

interface ElementIdArgs {
  rateIdInFocus?: string;
  rateIndexInFocus?: number;
  questionIndexInFocus?: number;
  scope?: "buyer" | "participant";
  fundDesignationInFocus?: boolean;
  /** The form field that was last changed (for scroll targeting based on user action) */
  lastChangedField?: string;
  /** Current state of advanced settings toggles (for resolving which preview section is visible) */
  allowInHonour?: boolean;
  displayDonorList?: boolean;
}

type ElementIdResolver = string | ((args: ElementIdArgs) => string | undefined) | null;

const SCROLL_TO_TOP = null;

export const STEP_SCROLL_TARGET_MAP: Record<LiveEditorStepKey, ElementIdResolver> = {
  "campaign-life-cycle": SCROLL_TO_TOP,
  title: SCROLL_TO_TOP,
  date: SCROLL_TO_TOP,
  location: SCROLL_TO_TOP,
  description: "form-v2-description",
  color: SCROLL_TO_TOP,
  banner: SCROLL_TO_TOP,
  logo: SCROLL_TO_TOP,
  tickets: ({ rateIdInFocus }) =>
    rateIdInFocus ? `${TICKETING_SELECTION_V2_RATE_ID_PREFIX}${rateIdInFocus}` : undefined,
  "add-donation": "extra-donation-amount-input",
  "add-ons": "extra-donation-amount-input",
  "discount-codes": "discount-code-field",
  "business-model": "tip-section-input",
  "thank-you-email": null,
  "thank-you-email-not-winner": null,
  "automated-communications": null,
  questions: ({ questionIndexInFocus, scope, rateIndexInFocus, fundDesignationInFocus }) => {
    if (fundDesignationInFocus) {
      return "fund-designation";
    }
    if (scope === "buyer") {
      return `buyer-question-${questionIndexInFocus ?? 0}`;
    }

    return `rate-${rateIndexInFocus ?? 0}-group-0-participant-0-question-${questionIndexInFocus ?? 0}`;
  },
  "e-ticket": null,
  "quickbooks-mapping": null,
  "advanced-settings": ({ lastChangedField, allowInHonour, displayDonorList }) => {
    if (lastChangedField === "dafEnabled") {
      return "payment-method-daf";
    }
    if (lastChangedField === "chequeEnabled") {
      return "payment-method-cheque";
    }
    if (lastChangedField === "allowInHonour") {
      return "in-honour-section";
    }
    if (lastChangedField === "displayDonorList") {
      return displayDonorList || !allowInHonour ? "donors-list-block" : "in-honour-section";
    }
    // Default (initial page load)
    return allowInHonour ? "in-honour-section" : "donors-list-block";
  },
  review: null,
  "tax-receipts": null,
  "fundraising-goal": SCROLL_TO_TOP,
  prizes: "ticketing-prizes",
  "order-fulfillment": "fulfillment-section",
  "suggested-donation-amounts": "amounts",
  "p2p-settings": "p2p-primary-campaign-actions",
};

export interface ScrollContext {
  rateIdInFocus?: string;
  rateIndexInFocus?: number;
  questionIndexInFocus?: number;
  scope?: "buyer" | "participant";
  fundDesignationInFocus?: boolean;
  targetElementId?: string;
  /** The form field that was last changed (for scroll targeting based on user action) */
  lastChangedField?: string;
  /** Current state of advanced settings toggles (for resolving which preview section is visible) */
  allowInHonour?: boolean;
  displayDonorList?: boolean;
}

export const getScrollTopStepTarget = (
  iframe: HTMLIFrameElement,
  currentStepKey: LiveEditorStepKey,
  context?: ScrollContext
): number | null => {
  if (!iframe.contentWindow) {
    return null;
  }

  const elementIdResolver = STEP_SCROLL_TARGET_MAP[currentStepKey];
  const resolvedElementId =
    typeof elementIdResolver === "function" ? elementIdResolver(context ?? {}) : elementIdResolver;

  const elementId = context?.targetElementId ?? resolvedElementId;

  if (!elementId) {
    return 0;
  }

  const targetElement = iframe.contentDocument?.getElementById(elementId);
  if (!targetElement) {
    return null;
  }

  const elementTop = targetElement.getBoundingClientRect().top;
  const iframeScrollTop = iframe.contentWindow.scrollY;
  const iframeHeight = iframe.contentWindow.innerHeight;
  const elementHeight = targetElement.getBoundingClientRect().height;

  // Position the element in the middle of the viewport
  const halfViewportHeight = iframeHeight / 2;
  return Math.max(iframeScrollTop + elementTop - halfViewportHeight + elementHeight / 2, 0);
};
